/**
 * @fileoverview Responsible User Display Component
 *
 * Simple component that displays user avatar and name using the users context.
 * This is the simple approach used throughout the app.
 *
 * @module RenovationProject/Components/ResponsibleUserDisplay
 */

import React from 'react';
import { Avatar } from '@heroui/react';
import { User } from '@phosphor-icons/react';
import { useUsers } from 'shared/contexts/users.context';

interface ResponsibleUserDisplayProps {
  userId: string | null | undefined;
}

export const ResponsibleUserDisplay: React.FC<ResponsibleUserDisplayProps> = ({ userId }) => {
  const { users } = useUsers();

  // Extract string ID from userId (handle both string and object cases)
  let extractedUserId: string | null = null;

  if (userId) {
    if (typeof userId === 'string') {
      extractedUserId = userId;
    } else if (typeof userId === 'object' && 'id' in userId) {
      // If somehow an object was passed, extract the ID
      extractedUserId = (userId as any).id;
      console.warn('ResponsibleUserDisplay received an object instead of string ID:', userId);
    }
  }

  if (!extractedUserId) {
    return (
      <span className="text-sm text-neutral-secondary-mid italic">
        Geen verantwoordelijke
      </span>
    );
  }

  // Find user in context
  const user = users.find((u) => u.id === extractedUserId);

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-200">
          <User size={14} className="text-neutral-500" />
        </div>
        <span className="text-sm text-neutral-600">{extractedUserId}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {user.image_url ? (
        <Avatar
          src={user.image_url}
          className="h-7 w-7"
          size="sm"
        />
      ) : (
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-200 text-xs font-medium text-neutral-600">
          {user.first_name?.[0]}{user.last_name?.[0]}
        </div>
      )}
      <div className="flex flex-col">
        <span className="text-sm font-medium text-neutral-800">
          {user.first_name} {user.last_name}
        </span>
      </div>
    </div>
  );
};
